/* Yoreflix Admin — colour tokens.
 *
 * MIMICS MONZA (docs/portals/design-guidelines.md §3): the taxonomy, the role/shade names and
 * the shade->token map are David's, lifted from monzadev's `theme-color-inventory.csv` and
 * `packages/client/src/theme/{palette,shadcn}.ts`. What is NOT lifted is monza's runtime theme
 * ENGINE (DB-authored themes, resolveTheme, light/dark auto-reversal): the admin portal has one
 * fixed theme, so the engine's output is written here directly as static custom properties. If a
 * second theme is ever wanted, this file is the seam — nothing reads a colour any other way.
 *
 * TWO TIERS:
 *   Tier 1 — the ROLE RAMPS. Nine named shades, light -> dark (monza's SHADE_KEYS, in order).
 *   Tier 2 — the SEMANTIC roles (shadcn variable names). Components only ever read these.
 *
 * Tier 2 uses shadcn's variable names deliberately: it is what the copied monza components
 * (TopbarSys, and later StrataGrid and the shadcn primitives) already read, so they port
 * unedited. Renaming them would mean re-editing every component we copy, forever.
 *
 * THE STRUCTURAL RULE (David, via the CSV): neutral structure sources from the MONOCHROME ramp
 * — surfaces, borders and all body/label text read clean — and the PRIMARY ramp is reserved for
 * the interactive accents (CTA, focus ring, hover, selection). That is where the brand green
 * lives. Text and borders sit on Monochrome, never on Primary.
 */

:root {
  /* ============ Tier 1 — role ramps (9 shades, light -> dark) ============ */

  /* PRIMARY — Yoreflix green. Hue 146 is the brand hue: it is the hue of BOTH viewer greens
     (--c-green-400 #74ac8c and --c-green-700 #355f47 are the same hue at different lightness),
     so this ramp is the viewer's green extended into a full scale rather than a new colour.
     Base is the brand green itself; Dark/VeryDark/NearBlack match the viewer's 700/800/900. */
  --primary-hint: hsl(146 30% 96%); /* hover / highlight tint */
  --primary-verylight: hsl(146 28% 89%); /* selection / muted bands */
  --primary-light: hsl(146 27% 78%);
  --primary-mediumlight: hsl(146 26% 66%); /* blocked/disabled CTA */
  --primary-base: hsl(146 25% 57%); /* = #74ac8c — the viewer's brand green */
  --primary-mediumdark: hsl(146 26% 43%);
  --primary-dark: hsl(146 28% 29%); /* = #355f47 — the viewer's deep green; our CTA (see below) */
  --primary-verydark: hsl(146 30% 20%);
  --primary-nearblack: hsl(146 32% 11%);

  /* MONOCHROME — the neutral structure ramp: every surface, border and text colour. */
  --mono-hint: #fafafa; /* page / card / popover / chrome surfaces */
  --mono-verylight: #f5f5f5;
  --mono-light: #e5e5e5; /* borders / inputs */
  --mono-mediumlight: #d4d4d4;
  --mono-base: #737373;
  --mono-mediumdark: #525252; /* muted / secondary text */
  --mono-dark: #404040;
  --mono-verydark: #262626;
  --mono-nearblack: #0a0a0a; /* body text / foregrounds */

  /* ACCENT2 — amber. Warning / "active unit of work" states (monza's Accent2 role). */
  --accent2-hint: #fffbeb;
  --accent2-base: #e99904;
  --accent2-nearblack: #92400e;

  /* DESTRUCTIVE — red. Semantic, NOT a Primary shade (the CSV flags this explicitly). */
  --destructive-hint: #fef2f2;
  --destructive-base: #c90000;
  --destructive-dark: #b91c1c;

  /* ============ Tier 2 — semantic roles (shadcn names) ============ */

  /* Surfaces — Monochrome:Hint, EXCEPT the page itself. David: white panels + menu.
     ★ --background is a faint green WASH (David, 2026-07-22) so the inner page reads as a
     slightly tinted area inside white chrome. It is deliberately the ONLY tinted surface:
     --card / --popover / --sidebar / --topbar-bg all stay opaque white, which is what makes
     panels and the menu stand off the page rather than melting into it.
     ⚠ It is an ALPHA colour, so it COMPOSITES. `body` paints it once and AdminShell's pageArea
     paints it again over the top, so the page area lands at roughly double the stated 2.5% while
     the sign-in screen (body only) sits at 2.5%. That is the intended look — chrome white, page
     tinted — but it is why this must not be "simplified" to an opaque hex: the two-layer
     composite IS the effect. */
  --background: rgba(0, 53, 0, 0.035);
  --foreground: var(--mono-nearblack);
  --card: var(--mono-hint);
  --card-foreground: var(--mono-nearblack);
  --popover: var(--mono-hint);
  --popover-foreground: var(--mono-nearblack);

  /* ★ The CHROME surface. The topbar and the side menu are ONE surface that happens to wrap the
     page in an L, and are never themed apart (David's rule, and the reason monza's topbar has
     its own var: it used to be hardcoded to --background and silently tracked the PAGE colour).
     Both read from here. */
  --sidebar: var(--mono-hint);
  --sidebar-foreground: var(--mono-nearblack);
  --topbar-bg: var(--sidebar);

  /* Borders / inputs — Monochrome:Light. */
  --border: var(--mono-light);
  --input: var(--mono-light); /* the input BORDER */
  /* ★ PANEL — the fill behind a grouped block of fields (Content's "Ratings & Dates" and its
     siblings). A real token rather than a literal at the call site: the page background is now
     tinted, and a panel needs its own surface to read as a block rather than as a hairline
     rectangle drawn on the page. */
  --panel: #f3f3f3;

  --input-bg: var(--card); /* the input FILL — a real token, never `transparent`: a transparent
                              input reads as whatever tint it happens to sit on. */
  --input-fg: var(--mono-nearblack);

  /* Muted / secondary / hover — the Primary ramp's light end. */
  --muted: var(--primary-verylight); /* section + table header bands */
  --muted-foreground: var(--mono-mediumdark); /* secondary text */
  /* ★ SELECTION, darkened 2026-07-23 with the deeper --background tint. The page is no longer
     near-white, so the old Primary:VeryLight (89%) sat too close to the surface it had to stand
     out from. Written as a literal for the same reason --nav-hover below is: a color-mix() off
     the ramp emits a plain-var FALLBACK at build time that would undo the adjustment. The
     ordering that matters is preserved — selection stays DARKER than hover.

     ⚠ SEPARATION, not just order (David, 2026-07-23). At 85% against a 89% hover the two were
     only 4 points apart and read as the same fill when they sat side by side in a list. Now 9
     points apart, which is enough to tell "the row I am on" from "the row under the cursor"
     without either becoming a heavy block. */
  --secondary: hsl(146 28% 80%); /* selected row */
  --secondary-foreground: var(--mono-nearblack);
  --accent: hsl(146 30% 92%); /* row / option / icon-button hover — darkened with --background */
  --accent-foreground: var(--mono-nearblack);

  /* Side-menu option hover. --accent (Primary:Hint) is only 4% off white and all but vanishes on
     the near-white chrome, so the menu takes its own hover: a NOTCH lighter than --secondary (the
     selected option) rather than a notch off the surface. Derived FROM --secondary so the two can
     never drift apart — hover always reads as "the selected tint, not yet selected". Written as a
     literal rather than a color-mix() off --secondary: the build emits a plain-var FALLBACK for
     color-mix, and that fallback would paint hover IDENTICAL to selection. Same hue/sat as the
     Primary ramp, lightness parked between VeryLight (89%, selection) and Hint (96%). */
  --nav-hover: hsl(146 28% 89%);

  /* ★ PRIMARY CTA — Primary:DARK, not Primary:Base. A DELIBERATE one-step deviation from monza's
     shade->token map ("Primary Base -> primary CTA"), forced by our hue and worth stating:
     monza's Primary is a blue whose Base is dark enough to carry near-white text, but the
     Yoreflix green's Base (#74ac8c) is tuned to sit on the viewer's near-BLACK background. On
     near-white it reaches only ~2.5:1 against --primary-foreground — a fail. Primary:Dark
     (#355f47) reaches ~7:1 (AAA) and is equally a Yoreflix brand green. The CSV already reaches
     the same conclusion for text ("Base can be too light for text on light bg; use Dark"); this
     applies it to the fill as well. Hover/press therefore step DOWN the ramp from Dark. */
  --primary: var(--primary-dark);
  --primary-foreground: var(--mono-hint); /* near-white on the green, per the CSV */
  --primary-hover: var(--primary-verydark); /* a darker STEP, never an opacity fade */
  --primary-active: var(--primary-nearblack);
  --primary-blocked: var(--primary-mediumlight); /* the greyed, un-clickable Save */

  /* Accent text / links — Primary:Dark (Base is too light on a near-white page). */
  --primary-text: var(--primary-dark);

  /* Focus ring — Primary. Distinct from hover, and solid (never a translucent ring). */
  --ring: var(--primary-dark);
  --ring-width: 2px;

  /* Status. */
  --destructive: var(--destructive-base);
  --destructive-foreground: var(--mono-hint);
  /* Hover fill for a destructive OUTLINE button (Delete / Cancel now / Refund). The red stays in
     the border and the label; the fill is the ramp's hint end, so hovering Delete never looks
     like Delete already fired. The green --nav-hover would be a lie on a destructive control. */
  --destructive-hover: var(--destructive-hint);
  --warning: var(--accent2-base);
  --success: #109410;

  /* The modal scrim. THE ONE SANCTIONED TRANSLUCENCY (the CSV flags it as the exception): a
     scrim's whole job is to let the page show through dimmed, so it cannot be a flat shade. */
  --scrim: rgba(0, 0, 0, 0.4);

  /* The overlay scrollbar (read by OverlayScrollbar.tsx). */
  --overlay-scrollbar-thumb: var(--mono-base);
  --overlay-scrollbar-track: rgba(0, 0, 0, 0.07);

  /* Geometry. */
  --radius: 6px;
}
